/* eslint-disable no-console */
/* eslint-disable prefer-promise-reject-errors */
import qs from 'qs';
import axios, { Method } from 'axios';
import { getEndpointUrl, getOptionalIdField, shouldUnwrapData } from '@/config/ConfigProvider';
import {
  createPatchFromAssociation,
  formatRequestByResource,
  remapIdField,
  restoreRemappIdField,
} from './fieldRemapper';

type SortParam = {
  by: string;
  desc: boolean;
};

export type ListResult = {
  data?: any[];
  cursor?: string;
  total?: number;
};

export type PaginationParam = {
  page?: number;
  perPage?: number;
  lastItem?: {
    key: string;
    id: string;
  };
  cursor?: string;
  limit?: number;
};

export type FilterParam = {
  [key: string]: string | string[];
};

export type Params = {
  filter: FilterParam;
  pagination: PaginationParam;
  sort: SortParam[];
  include?: { [key: string]: string };
  cursor?: string;
};

const getQueryString = (params: Params, resource?: string): string => {
  // Remove the __ob_ Observer field from the query params object
  const cleanedParam: Params = JSON.parse(JSON.stringify(params));
  // let filter;
  const filter = Object.getOwnPropertyNames(cleanedParam.filter)
    .filter((prop) => prop !== 'q')
    .flatMap((prop) => {
      const value = cleanedParam.filter[prop];
      if (Array.isArray(value)) {
        return (value as string[]).map((val) => `${prop}:${val}`);
      }
      return [`${prop}:${value}`];
    });
  // if we are listing and don't have any sort, default to use lastUpdatedAt
  if (
    (resource === 'books' ||
      resource === 'audio-fragments' ||
      resource === 'content-image' ||
      resource === 'articles' ||
      resource === 'materials') &&
    cleanedParam.sort?.length <= 0 &&
    !cleanedParam.filter.q
  ) {
    cleanedParam.sort.push({ by: 'lastUpdatedAt', desc: true });
  }
  const queryTmp = {
    q: cleanedParam.filter.q,
    filter,
    sort: cleanedParam.sort?.map((fieldSortParam) => `${fieldSortParam.by}${fieldSortParam.desc ? ':desc' : ''}`),
  };
  let query;
  if (
    [
      'collections',
      'collections-us',
      'exercises',
      'publishers',
      'languages',
      'series',
      'views',
      'categories',
      'areas',
      'keywords',
      'contributors',
      'curriculum-goals',
      'translations',
      'scopes',
    ].includes(resource ?? '')
  ) {
    query = { ...queryTmp, ...cleanedParam.pagination };
  } else {
    // content-search expects "size" param, not limit
    const { limit, ...paginationWithoutLimit} = cleanedParam.pagination ?? {};
    const pagination = limit ? { ...paginationWithoutLimit, size: limit } : paginationWithoutLimit;
    query = { ...queryTmp, pagination: pagination };
  }

  const urlQs = qs.stringify(query, { arrayFormat: 'repeat' });
  return urlQs ? `?${urlQs}` : '';
};

type Headers = { [key: string]: string };

const getHeaders = (customHeaders?: Headers): Headers => {
  const headers: Headers = {};
  headers['Content-Type'] = 'application/json';
  // headers.Origin = 'https://localhost';
  return { ...headers, ...customHeaders };
};

const getUpdateRequestHTTPMethod = (resource: string, isPatchRequest: boolean): Method =>
  isPatchRequest || ['views', 'exercises'].includes(resource) ? 'PATCH' : 'PUT';

function sendRequest(
  method: Method,
  resource: string,
  operation?: string,
  include?: Params['include'],
  queryString?: string,
  data?: any,
) {
  return axios({
    method,
    url: `${getEndpointUrl(resource, operation, include)}${queryString}`,
    headers: getHeaders(),
    data,
  });
}
function makeUpdateRequest(resource: string, id: string | number, data: unknown): Promise<{ data: unknown }> {
  const isPatchObject =
    Array.isArray(data) && typeof data[0] === 'object' && Object.prototype.hasOwnProperty.call(data[0], 'op');
  return sendRequest(
    getUpdateRequestHTTPMethod(resource, isPatchObject),
    resource,
    'update',
    undefined,
    resource === 'scopes' ? '' : `/${id}`,
    data,
  );
}

export default () => ({
  getList: (resource: string, params: Params): Promise<ListResult> =>
    sendRequest('get', resource, 'list', params.include, getQueryString(params, resource), undefined)
      .then((response) => {
        const data = shouldUnwrapData(resource) ? response.data : response;
        return {
          data: remapIdField(data.data, getOptionalIdField(resource)) as any[],
          total: data.total ?? (data.cursor ? data.data.length + 1 : -1),
          cursor: data.cursor,
        };
      })
      .catch((response) => Promise.reject({ status: response.status, message: response.statusText })),
  getMany: (resource: string, params: { ids: string[] | number[]; include?: { [key: string]: string } }) => {
    const data = Promise.all(
      params.ids.map((id) =>
        sendRequest('get', resource, 'get', params.include, id.toString()).then((response) => response.data),
      ),
    );
    return { data: remapIdField(data, getOptionalIdField(resource)) };
  },
  getOne: (resource: string, params: { id: string | number; include?: { [key: string]: string } }) =>
    sendRequest('get', resource, 'get', params.include, `/${params.id}`).then((response) =>
      remapIdField(response, getOptionalIdField(resource)),
    ),
  create: (
    resource: string,
    params: {
      data: unknown;
      include?: { [key: string]: string };
      queryParams?: { [key: string]: string };
      customHeaders?: Headers;
    },
  ) => {
    formatRequestByResource(params, resource);
    const query = qs.stringify(params.queryParams);
    return axios
      .post(getEndpointUrl(resource, 'post', params.include).concat(query ? `?${query}` : ''), params.data, {
        headers: getHeaders(params.customHeaders),
      })
      .catch((error) =>
        Promise.reject({
          status: error.status,
          message: error.response.data || error.response.statusText || error.message,
        }),
      );
  },
  update: (
    resource: string,
    params: { id: string | number; data: unknown; include?: { [key: string]: string } },
  ): Promise<{ data: unknown }> => {
    console.log('update', resource, params);
    restoreRemappIdField(params.data, getOptionalIdField(resource));
    formatRequestByResource(params, resource);
    return makeUpdateRequest(resource, params.id, createPatchFromAssociation(params.data));
  },
  updateMany: (
    resource: string,
    params: { ids: string[] | number[]; data: unknown; include?: { [key: string]: string } },
  ) => Promise.all(params.ids.map((id) => makeUpdateRequest(resource, id, params.data))),
  delete: (resource: string, params: { id: string | number }) =>
    axios.delete(`${getEndpointUrl(resource)}/${params.id}`, {
      headers: getHeaders(),
    }),
  deleteMany: (resource: string, params: { ids: string[] | number[] }) =>
    Promise.all(
      params.ids.map((id) =>
        axios
          .delete(`${getEndpointUrl(resource)}/${id}`, {
            headers: getHeaders(),
          })
          .then(() => Promise.resolve()),
      ),
    ),
});
